/** @format */
import {useEffect, useState} from 'react';

import AddIcon from '@mui/icons-material/Add';
import {Box, LinearProgress} from '@mui/material';
import Alert from '@mui/material/Alert';
import {format} from 'date-fns';
import {ru} from 'date-fns/locale';

import {useCompanyContext} from 'contexts/CompanyContext';
import {useSnackbar} from 'components/SnackbarNew';

import {
  AddNewNoteButton,
  NoteContainerHeader,
  ContainerHeaderNotesCount,
  NoteContainer,
} from './styles';
import {
  useCreateNoteMutation,
  useDeleteNoteMutation,
  useGetNotesQuery,
  useUpdateNoteMutation,
} from '../graphql/__generated__';
import NoteTextField from './Note';

interface Props {
  notesCount?: number;
  date?: string;
  refetchNotes?: () => void;
  isAggregated?: boolean;
}

const NoteModal = ({notesCount, date, refetchNotes, isAggregated}: Props) => {
  const {
    currentCompany: {id: companyId},
  } = useCompanyContext();

  const [isNewNoteOpen, setIsNewNoteOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const {showSnack, Snack} = useSnackbar({variant: 'success'});

  const [createNoteMutation] = useCreateNoteMutation();
  const [updateNoteMutation] = useUpdateNoteMutation();
  const [deleteNoteMutation] = useDeleteNoteMutation();

  const noteDate = date || format(new Date(), 'yyyy-MM-dd');
  const {data, loading, error, refetch} = useGetNotesQuery({
    variables: {
      startDate: noteDate,
      endDate: noteDate,
    },
    skip: notesCount === 0 && !hasChanges,
  });

  const notes = data?.currentUser?.currentCompany?.digitalCastNotes || [];

  const filteredNotes = notes.filter(note => note.noteDate.startsWith(noteDate));

  useEffect(() => {
    if (notesCount === 0 && !isAggregated && !hasChanges) {
      setIsNewNoteOpen(true);
    }

    // Вызываем refetchNotes при размонтировании, если были изменения
    return () => {
      if (hasChanges && refetchNotes) {
        refetchNotes();
      }
    };
  }, [notesCount, isAggregated, hasChanges, refetchNotes]);

  const handleCreate = async (noteInput: {companyId: string; noteDate: string; text: string}) => {
    await createNoteMutation({variables: {noteInput}});
    setIsNewNoteOpen(false);
    showSnack('Заметка добавлена');
    await refetch();
    setHasChanges(true);
  };

  const handleUpdate = async (noteInput: {id: string; text: string}) => {
    await updateNoteMutation({variables: {noteInput}});
    showSnack('Заметка обновлена');
    setHasChanges(true);
  };

  const handleDelete = async ({id, isNew}: {id: string; isNew?: boolean}) => {
    if (isNew) {
      setIsNewNoteOpen(false);
      return;
    }

    await deleteNoteMutation({variables: {id}});
    showSnack('Заметка удалена');
    await refetch();
    setHasChanges(true);
  };

  const handleNewNoteButtonClick = () => {
    setIsNewNoteOpen(true);
  };

  if (error) {
    return <Alert severity='error'>{error?.message}</Alert>;
  }
  if (loading) {
    return (
      <NoteContainer isAggregated={isAggregated}>
        <LinearProgress style={{flex: 1}} />
      </NoteContainer>
    );
  }

  const formattedDate = format(new Date(noteDate), 'd MMMM', {locale: ru});

  return (
    <NoteContainer isAggregated={isAggregated}>
      <NoteContainerHeader>
        <Box>
          {formattedDate}
          {filteredNotes.length > 0 && (
            <ContainerHeaderNotesCount>Заметок: {filteredNotes.length}</ContainerHeaderNotesCount>
          )}
        </Box>
        {!isNewNoteOpen && (
          <AddNewNoteButton onClick={handleNewNoteButtonClick}>
            <AddIcon />
            Добавить заметку
          </AddNewNoteButton>
        )}
      </NoteContainerHeader>

      {isNewNoteOpen && (
        <NoteTextField
          noteDate={noteDate}
          onCreate={handleCreate}
          onDelete={handleDelete}
          companyId={companyId}
          isNew
        />
      )}

      {filteredNotes.map(note => (
        <NoteTextField
          key={note.id}
          note={note}
          noteDate={noteDate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          companyId={companyId}
        />
      ))}
      <Snack />
    </NoteContainer>
  );
};

export default NoteModal;
